import React, {ReactNode, useEffect, useState} from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';
import {Trans} from '@lingui/react';

import MassOperationsMenu from 'shared/ui/icons/MassOperationsMenu';
import MassOperationsMenuClose from 'shared/ui/icons/MassOperationsMenuClose';
import useBreakpoint from 'shared/lib/hooks/useBreakpoint';

import styles from './MassOperations.module.scss';

export interface IMassOperationAction {
    value: string;
    label: string;
    icon: ReactNode;
    title: string;
    ignoreSelection?: boolean;
}

export interface IMassOperationsModalProps {
    open: boolean;
    action?: IMassOperationAction;
    handleClose: () => void;
    onSuccess: () => void;
}

interface IMassOperationsProps {
    count: number;
    actions: IMassOperationAction[];
    onClear: () => void;
    toggleMassOperationsMode: () => void;
    onClose: () => void;
    // eslint-disable-next-line no-unused-vars
    renderModal: (modalProps: IMassOperationsModalProps) => ReactNode;
}

const MassOperations: React.FC<IMassOperationsProps> = ({
    count,
    actions,
    onClear,
    toggleMassOperationsMode,
    onClose,
    renderModal,
}) => {
    const [openMenu, setOpenMenu] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedAction, setSelectedAction] = useState<IMassOperationAction | undefined>();
    const breakpoint = useBreakpoint();
    const isTitle = breakpoint !== 'small' && breakpoint !== 'mini';

    useEffect(() => {
        if (count > 0) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setOpenMenu(true);
        } else {
            setOpenMenu(false);
        }
    }, [count]);

    const handleActionClick = (action: IMassOperationAction) => {
        setSelectedAction(action);
        setShowModal(true);
        setOpenMenu(false);
    };

    const handleCloseModal = () => {
        onClear();
        setShowModal(false);
        setSelectedAction(undefined);
        setOpenMenu(false);
    };

    const handleOperationSuccess = () => {
        onClear();
        setOpenMenu(false);
    };

    const handleOpenMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpenMenu(true);
        toggleMassOperationsMode();
    };

    const handleCloseMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpenMenu(false);
        onClear();
        toggleMassOperationsMode();
        onClose();
    };

    const handleButtonClick = () => {
        setOpenMenu(!openMenu);
        toggleMassOperationsMode();
    };

    return (
        <div className={styles.container}>
            <button
                className={cn(styles.button, openMenu && styles.activeButton)}
                type="button"
                onClick={handleButtonClick}
            >
                <span onClick={handleOpenMenu} className={styles.iconWrapper}>
                    {
                        !openMenu
                            ? <MassOperationsMenu />
                            : <span onClick={handleCloseMenu}>
                                <MassOperationsMenuClose />
                            </span>
                    }
                </span>

                {!openMenu ? (
                    <span onClick={handleOpenMenu}>
                        <Trans id="mass.operation.title" message="Массовые операции" />

                    </span>

                ) : (
                    <>
                        {t`Выбрано`}: <span className={styles.count}>{count}</span>
                    </>
                )}

            </button>
            {
                openMenu && (
                    <div className={styles.actions}>
                        {actions.map((item, index) => {
                            const disabled = count === 0 && !item.ignoreSelection;

                            return (
                                <button
                                    key={index}
                                    className={cn(styles.item, disabled && styles.disabled)}
                                    onClick={() => handleActionClick(item)}
                                    disabled={disabled}
                                >
                                    <span>{item.icon}</span>
                                    <span>{isTitle ? item.title : ''}</span>
                                </button>
                            );
                        })}
                    </div>
                )
            }
            {renderModal({
                open: showModal,
                action: selectedAction,
                handleClose: handleCloseModal,
                onSuccess: handleOperationSuccess,
            })}
        </div>
    );
};

export default MassOperations;
